import { isNil, get, findKey, isEmpty } from 'lodash';
import { NODE_TYPES } from './taskConstants';

/**
 * Gets the node id.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The id for the node or empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getId = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'id', '');
};

/**
 * Gets the node name.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The name for the node or empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getName = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'argName', '');
};

/**
 * Gets the node label.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The label for the node or empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getLabel = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'nodeLabel', '');
};

/**
 * Gets the node status.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The status for the node or empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getStatus = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'status', '');
};

/**
 * Gets the node comments.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The comments for the node or empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getComments = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'comments', '');
};

/**
 * Gets the node progress.
 *
 * @param {object} graphNode - The node object.
 * @returns {number} The progress for the node or 0 if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getProgress = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'progress', 0);
};

/**
 * Gets the node type.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The type for the node or empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getType = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'nodeType', '');
};

/**
 * Gets the node enum type.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The enum type for the node or empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getEnumType = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  const enumKey = findKey(NODE_TYPES, type => type === getType(graphNode));
  return enumKey;
};

/**
 * Checks if the node is a subtask.
 *
 * @param {object} graphNode - The node object.
 * @returns {boolean} True if the node is a subtask, false otherwise.
 * @throws {Error} If node is null or undefined.
 */
export const isSubTask = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'nodeType', '') === NODE_TYPES.SUBTASK;
};

/**
 * Gets the node icon.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The icon for the node.
 * @throws {Error} If node is null or undefined.
 */
export const getNodeIcon = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return NODE_TYPES.properties[getEnumType(graphNode)].iconType;
};

/**
 * Gets the node color.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The color for the node.
 * @throws {Error} If node is null or undefined.
 */
export const getNodeColor = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return NODE_TYPES.properties[getEnumType(graphNode)].iconColor;
};

/**
 * Checks if the node is a connection.
 *
 * @param {object} graphNode - The node object.
 * @returns {boolean} True if the node is a connection, false otherwise.
 * @throws {Error} If node is null or undefined.
 */
export const isConnection = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'nodeType', '') === NODE_TYPES.CONNECTION;
};

/**
 * Gets the node parameter list.
 *
 * @param {object} graphNode - The node object.
 * @returns {Array} The parameter list for the node or empty array if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getParamList = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'paramList', []);
};

/**
 * Gets the node source.
 *
 * @param {object} graphNode - The node object.
 * @returns {object} The source for the node or empty object if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getSource = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'connectionParams.source', {});
};

/**
 * Gets the node allowed source file types.
 *
 * @param {object} graphNode - The node object.
 * @returns {string[]} The allowed source file types for the node or empty array if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getAllowedSourceFileTypes = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'connectionParams.allowedFileTypes', []);
};

/**
 * Gets the node allowed source type for postgres sources.
 *
 * @param {object} graphNode - The node object.
 * @returns {string[]} The allowed source type for postgres sources for the node or empty array if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getAllowedSourcePGTypes = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'connectionParams.allowedPGTypes', []);
};

/**
 * Gets the node allowed map server element types.
 *
 * @param {object} graphNode - The node object.
 * @returns {string[]} The allowed map server element types for the node or empty array if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getAllowedMapServerElementTypes = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'connectionParams.allowedMapServerElementTypes', []);
};

/**
 * Checks if the node is temporal or not.
 *
 * @param {object} graphNode - The node object.
 * @returns {boolean} False if the node is not temporal, true otherwise.
 * @throws {Error} If node is null or undefined.
 */
export const isTemporal = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'connectionParams.temporal', true);
};

/**
 * Checks if the connection should create a map server element.
 *
 * @param {object} graphNode - The node object.
 * @returns {boolean} True if the connection should create a map server element, false otherwise.
 * @throws {Error} If node is null or undefined.
 */
export const shouldCreateMapServerElement = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'connectionParams.createMapServerElement', false);
};

/**
 * Gets the node map server elements results.
 *
 * @param {object} graphNode - The node object.
 * @returns {Array} The map server elements results for the node or empty array if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getMapServerElementResults = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'connectionParams.mapServerElements', []);
};

/**
 * Gets the node mapServerElementType.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The mapServerElementType for the node or empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const mapServerElementType = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'connectionParams.mapServerElementType', '');
};

/**
 * Checks if the creation of a map server element should be allowed.
 *
 * @param {object} graphNode - The node object.
 * @returns {boolean} True if the map server element creation should be allowed, false otherwise.
 * @throws {Error} If node is null or undefined.
 */
export const isMapServerElementAllowed = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  const allowedMapServerElementTypes = getAllowedMapServerElementTypes(graphNode);
  return !isEmpty(allowedMapServerElementTypes);
};

/**
 * Checks if map server element autorefreshes.
 *
 * @param {object} graphNode - The node object.
 * @returns {boolean} True if the map server element autorefreshes, false otherwise.
 * @throws {Error} If node is null or undefined.
 */
export const shouldMapServerElementAutoRefresh = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'connectionParams.autorefresh', false);
};

/**
 * Gets the map server element refresh latency.
 *
 * @param {object} graphNode - The node object.
 * @returns {number} The map server element refresh latency for the node or NaN if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getRefreshLatency = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'connectionParams.refreshLatency', NaN);
};

/**
 * Checks if the node is ready to send.
 *
 * @param {object} graphNode - The node object.
 * @returns {boolean} True is the node is ready to send, false otherwise.
 * @throws {Error} If node is null or undefined.
 */
export const isNodeReady = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'isNodeReady', false);
};

/**
 * Get the date when the process started.
 *
 * @param {object} graphNode - The node object.
 * @returns {boolean} The date when the process started or an empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getStartDate = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'startedAt', '');
};

/**
 * Get the date when the process finished.
 *
 * @param {object} graphNode - The node object.
 * @returns {string} The date when the process finished or an empty string if not found.
 * @throws {Error} If node is null or undefined.
 */
export const getFinishDate = graphNode => {
  if (isNil(graphNode)) {
    throw new Error('node not defined');
  }
  return get(graphNode, 'finishedAt', '');
};
